import { ConditionsNew } from '@retail/calculus/types';
import { getConditionBindingId } from '@retail/calculus/utils';

export type CustomerMarkupValue = NonNullable<ConditionsNew['customerMarkupCondition']>;

export interface DiscountMatrixCustomer {
  assortmentId: number;
  assortmentName: string;
}

export interface DiscountMatrix {
  rows: ConditionsNew[];
  customers: DiscountMatrixCustomer[];
  cells: Map<string, Map<number, CustomerMarkupValue>>;
}

export const getDiscountMatrixRowId = (condition: ConditionsNew): string =>
  condition.bindingType === 'ASSORTMENT'
    ? `${condition.supplier?.supplierNumber ?? ''}|ASSORTMENT|`
    : getConditionBindingId(condition);

export const buildDiscountMatrix = (conditions: ConditionsNew[]): DiscountMatrix => {
  const rows = new Map<string, ConditionsNew>();
  const customers = new Map<number, string>();
  const cells = new Map<string, Map<number, CustomerMarkupValue>>();

  conditions.forEach((condition) => {
    const rowId = getDiscountMatrixRowId(condition);
    if (!rows.has(rowId)) rows.set(rowId, condition);

    if (!condition.customerMarkupCondition) return;
    customers.set(condition.assortmentId, condition.assortmentName);

    const byCustomer = cells.get(rowId) ?? new Map<number, CustomerMarkupValue>();
    byCustomer.set(condition.assortmentId, condition.customerMarkupCondition);
    cells.set(rowId, byCustomer);
  });

  return {
    rows: Array.from(rows.values()),
    customers: Array.from(customers, ([assortmentId, assortmentName]) => ({
      assortmentId,
      assortmentName,
    })).sort((a, b) => a.assortmentName.localeCompare(b.assortmentName)),
    cells,
  };
};

export type DiscountMatrixEdits = Record<string, string>;

export interface DiscountMatrixChange {
  row: ConditionsNew;
  assortmentId: number;
  discountPercent: number;
}

export const getDiscountMatrixCellKey = (rowId: string, assortmentId: number): string =>
  `${rowId}::${assortmentId}`;

const parsePercent = (value: string): number | null => {
  const normalized = value.replace(/\s/g, '').replace(',', '.').replace('\u2212', '-');
  if (!normalized) return null;
  const percent = Number(normalized);
  return Number.isFinite(percent) ? percent : null;
};

export interface DiscountMatrixCellState {
  invalid: boolean;
  changed: boolean;
  percent: number | null;
}

export const getDiscountMatrixCellState = (
  original: number | undefined,
  edit: string | undefined
): DiscountMatrixCellState => {
  if (edit === undefined) return { invalid: false, changed: false, percent: original ?? null };
  const percent = parsePercent(edit);
  if (percent === null) {
    const invalid = edit.trim() !== '' || original !== undefined;
    return { invalid, changed: false, percent: null };
  }
  return { invalid: false, changed: percent !== original, percent };
};

export const getDiscountMatrixChanges = (
  { rows, cells }: DiscountMatrix,
  edits: DiscountMatrixEdits
): { changes: DiscountMatrixChange[]; hasInvalid: boolean } => {
  const changes: DiscountMatrixChange[] = [];
  let hasInvalid = false;
  const rowsById = new Map(rows.map((row) => [getDiscountMatrixRowId(row), row]));

  Object.entries(edits).forEach(([key, edit]) => {
    const separatorIndex = key.lastIndexOf('::');
    const rowId = key.slice(0, separatorIndex);
    const assortmentId = Number(key.slice(separatorIndex + 2));
    const row = rowsById.get(rowId);
    if (!row) return;

    const original = cells.get(rowId)?.get(assortmentId)?.discountPercent ?? undefined;
    const { invalid, changed, percent } = getDiscountMatrixCellState(original, edit);
    if (invalid) hasInvalid = true;
    if (changed && percent !== null) changes.push({ row, assortmentId, discountPercent: percent });
  });

  return { changes, hasInvalid };
};
